import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SiteChrome from "@/components/SiteChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { NATIONALITIES, flagUrl } from "@/lib/nationalities";
import AvatarBuilder from "@/components/AvatarBuilder";
import { AvatarSVG, DEFAULT_AVATAR } from "@/lib/avatarBuilder";
import { Play, Pencil, AlertCircle, Trophy, Award, Download, Lock, Ban } from "lucide-react";
import { toast } from "sonner";
import { usePlatformMessages } from "@/lib/usePlatformMessages";
import { useI18n } from "@/lib/i18n";
import FitText from "@/components/FitText";

function formatTime(s) {
  if (s == null) return "—";
  const sec = Number(s);
  const mm = Math.floor(sec / 60).toString().padStart(2, "0");
  const rest = (sec - Math.floor(sec / 60) * 60).toFixed(1).padStart(4, "0");
  return `${mm}:${rest}`;
}

export default function CompetitionLobby() {
  const { gameId } = useParams();
  const nav = useNavigate();
  const { auth, logout } = useAuth();
  const { m } = usePlatformMessages();
  const { t, lang } = useI18n();
  const [lobby, setLobby] = useState(null);
  const [name, setName] = useState("");
  const [school, setSchool] = useState("");
  const [nationality, setNationality] = useState("PT");
  const [avatarCfg, setAvatarCfg] = useState({ ...DEFAULT_AVATAR });
  const [showAvatarBuilder, setShowAvatarBuilder] = useState(false);
  const [teacherDialog, setTeacherDialog] = useState(false);
  const [teacherPwd, setTeacherPwd] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!auth?.credential_id) {
      nav("/competir");
      return;
    }
    api.get(`/competition/lobby/${auth.credential_id}?lang=${lang}`)
      .then((r) => {
        setLobby(r.data);
        const c = r.data.credential || {};
        setName(c.name || "");
        setSchool(c.school || "");
        setNationality((c.nationality || "PT").toUpperCase());
        if (c.avatar_cfg && typeof c.avatar_cfg === "object") {
          setAvatarCfg({ ...DEFAULT_AVATAR, ...c.avatar_cfg });
        }
      })
      .catch(() => {
        toast.error(m("lobby_load_error", null, "Erro ao carregar lobby"));
        nav("/competicao");
      });
  }, [auth, nav, lang]);

  const missing = [];
  if (!name.trim()) missing.push("Nome");
  if (!school.trim()) missing.push("Escola");
  if (!nationality?.trim()) missing.push("Nacionalidade");

  const persistPlayerInfo = async () => {
    setSaving(true);
    try {
      await api.put(`/competition/player-info/${auth.credential_id}`, {
        name: name.trim(),
        school: school.trim(),
        nationality: nationality.trim().toUpperCase(),
        avatar_cfg: avatarCfg,
      });
      localStorage.setItem("player_avatar_cfg", JSON.stringify(avatarCfg));
      localStorage.setItem("player_name", name.trim());
      localStorage.setItem("player_nationality", nationality);
      localStorage.setItem("player_school", school.trim());
      sessionStorage.setItem("player_name", name.trim());
      sessionStorage.setItem("player_nationality", nationality);
      sessionStorage.setItem("player_school", school.trim());
    } finally {
      setSaving(false);
    }
  };

  const onPlay = async () => {
    if (missing.length) {
      toast.error(m("fill_required_fields", { fields: missing.join(", ") }, `Preenche: ${missing.join(", ")}`));
      return;
    }
    await persistPlayerInfo();
    const alreadyVerified = sessionStorage.getItem(`teacher_verified_${gameId}`) === "1";
    if (lobby?.game?.has_teacher_password && !alreadyVerified) {
      setTeacherDialog(true);
    } else {
      sessionStorage.setItem(`teacher_verified_${gameId}`, "1");
      if ((lobby?.credential?.attempts_available ?? 0) <= 0) {
        toast.error(m("no_attempts", null, "Sem tentativas disponíveis."));
        return;
      }
      nav(`/jogar/${gameId}?mode=competition`);
    }
  };

  const onTeacherConfirm = async () => {
    setVerifying(true);
    try {
      await api.post(`/competition/verify-teacher/${gameId}`, { password: teacherPwd });
      setTeacherDialog(false);
      sessionStorage.setItem(`teacher_verified_${gameId}`, "1");
      if ((lobby?.credential?.attempts_available ?? 0) <= 0) {
        toast.error(m("no_attempts_exhausted", null, "Sem tentativas disponíveis. Esta credencial já esgotou as tentativas."));
        return;
      }
      nav(`/jogar/${gameId}?mode=competition`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || m("teacher_password_wrong", null, "Senha incorreta"));
    } finally {
      setVerifying(false);
    }
  };

  const downloadDiploma = async () => {
    try {
      const res = await api.get(`/diplomas/by-credential/${auth.credential_id}`, {
        params: { lang },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `diploma_${lobby?.credential?.username || "aluno"}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(m("diploma_download_error", null, "Não foi possível baixar o diploma"));
    }
  };

  if (!lobby) {
    return (
      <SiteChrome>
        <div className="h-[60vh] grid place-items-center text-slate-400">{t("common_loading")}</div>
      </SiteChrome>
    );
  }

  const { credential: c, game: g, attempts, diploma_available } = lobby;

  return (
    <SiteChrome>
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-[1fr_360px] gap-6">
          {/* LEFT: player form */}
          <div className="bg-white rounded-3xl shadow border border-slate-200 p-6">
            <h1 className="text-2xl font-[Fredoka] font-bold text-white mb-1" data-testid="lobby-title">
              {t("lobby_welcome", { username: c.username })}
            </h1>
            <p className="text-sm text-slate-600 mb-5">{t("lobby_confirm_hint")}</p>

            <div className="flex items-start gap-4 mb-5">
              <button
                data-testid="btn-edit-avatar-lobby"
                onClick={() => setShowAvatarBuilder(true)}
                className="relative w-20 h-20 rounded-2xl overflow-hidden border-2 border-slate-200 hover:border-sky-400"
              >
                <AvatarSVG cfg={avatarCfg} size={80} />
                <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-sky-500 text-white grid place-items-center"><Pencil className="w-3.5 h-3.5" /></span>
              </button>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{t("lobby_avatar_label")}</div>
                <div className="text-sm text-slate-600">{t("lobby_avatar_hint")}</div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div>
                <Label>{t("your_name")} *</Label>
                <Input data-testid="lobby-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <Label>{t("school")} *</Label>
                <Input data-testid="lobby-school" value={school} onChange={(e) => setSchool(e.target.value)} />
              </div>
            </div>
            <div className="mb-5">
              <Label>{t("your_nationality")} *</Label>
              <Select value={nationality} onValueChange={setNationality}>
                <SelectTrigger data-testid="lobby-nationality">
                  <SelectValue placeholder={t("select_placeholder")} />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {NATIONALITIES.map((n) => (
                    <SelectItem key={n.code} value={n.code}>
                      <span className="inline-flex items-center gap-2">
                        {flagUrl(n.code) && <img src={flagUrl(n.code)} alt={n.code} className="w-4 h-3 object-cover rounded-sm" />}
                        {n.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {missing.length > 0 && (
              <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 text-sm" data-testid="missing-fields">
                <AlertCircle className="w-4 h-4" /> {t("lobby_missing_prefix")} <strong>{missing.join(", ")}</strong>
              </div>
            )}

            {(c.attempts_available ?? 0) <= 0 ? (
              <div className="space-y-3">
                <Button
                  data-testid="btn-save-profile-exhausted"
                  onClick={async () => {
                    if (missing.length) {
                      toast.error(m("fill_required_fields", { fields: missing.join(", ") }, `Preenche: ${missing.join(", ")}`));
                      return;
                    }
                    try {
                      await persistPlayerInfo();
                      toast.success(t("save_btn"));
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Erro ao gravar");
                    }
                  }}
                  disabled={missing.length > 0 || saving}
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full font-bold py-5"
                >
                  {t("save_btn")}
                </Button>
                <div
                  className="w-full bg-rose-50 border-2 border-rose-200 rounded-2xl px-4 py-5 flex items-start gap-3"
                  data-testid="participation-ended-block"
                >
                  <Ban className="w-6 h-6 text-rose-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-rose-800 font-semibold leading-snug">
                    {t("lobby_participation_ended")}
                  </div>
                </div>
              </div>
            ) : (
              <Button
                data-testid="btn-play-lobby"
                onClick={onPlay}
                disabled={missing.length > 0 || saving}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-full font-bold text-lg py-6"
              >
                <Play className="w-5 h-5 mr-2" /> {t("lobby_play_btn")}
              </Button>
            )}
            <button
              onClick={() => { logout(); nav("/"); }}
              className="block mx-auto mt-3 text-xs text-slate-500 hover:text-rose-600"
              data-testid="btn-logout-lobby"
            >
              {t("lobby_logout_btn")}
            </button>
          </div>

          {/* RIGHT: game info */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow border border-slate-200 p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">{t("lobby_game_label")}</div>
              <FitText
                as="div"
                maxPx={22}
                minPx={12}
                className="font-[Fredoka] font-bold text-slate-900"
                data-testid="lobby-game-name"
                title={g.name}
              >
                {g.name}
              </FitText>
              <div className="grid grid-cols-2 gap-2 mt-3 text-center">
                <div className="bg-sky-50 rounded-lg px-2 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold">{g.level_system === "tiers" ? t("lobby_tier_or_level") : t("lobby_level_only")}</div>
                  <FitText
                    as="div"
                    maxPx={20}
                    minPx={11}
                    className="font-bold text-sky-900"
                    data-testid="lobby-level"
                    title={
                      g.level_system === "tiers"
                        ? (() => {
                            const t2 = (c.tier_id && g.tiers?.find((x) => x.id === c.tier_id))
                              || g.tiers?.find((x) => Number(x.level) === Number(c.level));
                            return t2 ? `${t2.name} (${t("lobby_level_only")} ${t2.level ?? c.level ?? 1})` : `${t("lobby_level_only")} ${c.level ?? 1}`;
                          })()
                        : String(c.level ?? 1)
                    }
                  >
                    {g.level_system === "tiers"
                      ? (() => {
                          const t2 = (c.tier_id && g.tiers?.find((x) => x.id === c.tier_id))
                            || g.tiers?.find((x) => Number(x.level) === Number(c.level));
                          return t2 ? `${t2.name} (${t("lobby_level_only")} ${t2.level ?? c.level ?? 1})` : `${t("lobby_level_only")} ${c.level ?? 1}`;
                        })()
                      : (c.level ?? 1)}
                  </FitText>
                </div>
                <div className="bg-rose-50 rounded-lg px-2 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">{t("lobby_attempts_label")}</div>
                  <div className="text-xl font-bold text-rose-900" data-testid="lobby-attempts">{c.attempts_available}/{g.max_attempts}</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-amber-600" />
                <span className="font-bold text-slate-800 text-sm">{t("lobby_previous_results")}</span>
              </div>
              {attempts.length === 0 ? (
                <div className="text-sm text-slate-500 italic" data-testid="no-results-yet">{t("lobby_no_results")}</div>
              ) : (
                <ul className="space-y-1 text-sm" data-testid="results-list">
                  {attempts.slice(0, 5).map((a, i) => (
                    <li key={a.id || `attempt-${i}-${a.time_seconds || 0}`} className="flex justify-between items-center border-b border-slate-100 py-1 last:border-0">
                      <span className={a.status === "won" ? "text-emerald-700 font-bold" : "text-rose-600 font-bold"}>
                        {a.status === "won" ? t("lobby_win_label") : t("lobby_loss_label")}
                      </span>
                      <span className="font-mono text-slate-700">{a.time_seconds != null ? formatTime(a.time_seconds) : "—"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-4 h-4 text-violet-600" />
                <span className="font-bold text-slate-800 text-sm">{t("lobby_diploma_label")}</span>
              </div>
              {diploma_available ? (
                <Button data-testid="btn-download-diploma" onClick={downloadDiploma} variant="outline" className="w-full">
                  <Download className="w-4 h-4 mr-2" /> {t("lobby_download_diploma")}
                </Button>
              ) : (
                <div className="text-sm text-slate-500 italic" data-testid="diploma-unavailable">{t("diploma_not_yet_available")}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAvatarBuilder && (
        <AvatarBuilder
          current={avatarCfg}
          onSave={(cfg) => { setAvatarCfg(cfg); setShowAvatarBuilder(false); }}
          onClose={() => setShowAvatarBuilder(false)}
        />
      )}

      <Dialog open={teacherDialog} onOpenChange={setTeacherDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-amber-600" /> {t("teacher_modal_title")}</DialogTitle>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900">
            {t("teacher_modal_body")}
          </div>
          <div>
            <Label>{t("teacher_password_label")}</Label>
            <Input
              data-testid="teacher-password-input"
              type="password"
              value={teacherPwd}
              onChange={(e) => setTeacherPwd(e.target.value)}
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") onTeacherConfirm(); }}
            />
          </div>
          <Button data-testid="btn-confirm-teacher" disabled={verifying || !teacherPwd} onClick={onTeacherConfirm} className="w-full bg-emerald-600 hover:bg-emerald-700">
            {t("teacher_confirm_btn")}
          </Button>
        </DialogContent>
      </Dialog>

    </SiteChrome>
  );
}
